// FIELD WORKER HOME — DESIGN PREVIEW B: "Daylight High-Vis" (light neo-brutalist)
// Self-contained mock. Not wired to real data.
import {
  MapPin, Navigation, Clock, ShieldAlert, ClipboardCheck, Trophy,
  FileSignature, ClipboardList, Palmtree, Home, Briefcase, Wrench,
  CircleUser, ArrowRight, Sun,
} from "lucide-react";

const shadow = "shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]";
const crush = "active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform duration-75";

export default function FieldPreview2() {
  return (
    <div className="min-h-screen bg-zinc-200 py-6" data-testid="field-preview-2">
      <div className="max-w-[390px] mx-auto min-h-[820px] relative bg-[#F5F5F4] text-black border-2 border-black overflow-hidden pb-24">

        {/* Header */}
        <div className="bg-[#FFD700] border-b-2 border-black px-4 pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.25em]">Fieldlink · Mon 28 Jul</div>
              <h1 className="font-display text-3xl font-black uppercase tracking-tight leading-none mt-1">Mōrena, Ben</h1>
            </div>
            <div className={`w-11 h-11 rounded-xl bg-white border-2 border-black flex items-center justify-center ${shadow}`}>
              <Sun className="w-5 h-5" strokeWidth={2.5} />
            </div>
          </div>
          {/* Geofence pill */}
          <div className={`mt-3 flex items-center justify-between bg-white border-2 border-black rounded-xl px-3 py-2 ${shadow}`} data-testid="p2-geofence">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 border border-black" />
              <span className="text-xs font-black uppercase tracking-wider">On site · Kereru Rd</span>
            </div>
            <button className={`text-[10px] font-black uppercase tracking-wider bg-black text-white rounded-lg px-3 py-1.5 ${crush}`}>Sign out</button>
          </div>
        </div>

        <div className="px-4 pt-4 space-y-4">
          {/* Today's job — massive card */}
          <div className={`bg-white border-2 border-black rounded-2xl overflow-hidden ${shadow}`} data-testid="p2-runsheet">
            <div className="bg-black text-[#FFD700] px-4 py-2 flex items-center justify-between">
              <span className="text-xs font-black uppercase tracking-[0.2em]">Today's job</span>
              <span className="font-mono text-xs font-bold">J0017</span>
            </div>
            <div className="p-4">
              <div className="font-display text-xl font-black uppercase leading-tight">Stormwater upgrade — Stage 2</div>
              <div className="flex items-center gap-1.5 text-sm text-stone-600 mt-1.5 font-medium">
                <MapPin className="w-4 h-4" strokeWidth={2.5} /> 45 Kereru Rd, Papakura
              </div>
              <div className="mt-3 flex gap-2">
                {[["07:00", "start"], ["3/5", "tasks done"], ["2", "crew"]].map(([v, l]) => (
                  <div key={l} className="flex-1 bg-stone-100 border-2 border-black rounded-xl px-2 py-2 text-center">
                    <div className="font-display font-black text-xl leading-none">{v}</div>
                    <div className="text-[9px] font-bold uppercase tracking-wider text-stone-500 mt-1">{l}</div>
                  </div>
                ))}
              </div>
              <button className={`mt-3 w-full h-12 bg-[#FFD700] border-2 border-black rounded-xl font-black uppercase tracking-wider text-sm flex items-center justify-center gap-2 ${shadow} ${crush}`}>
                <Navigation className="w-4 h-4" strokeWidth={2.5} /> Get directions
              </button>
            </div>
          </div>

          {/* Hours + primary CTA combined */}
          <div className={`bg-white border-2 border-black rounded-2xl p-4 ${shadow}`} data-testid="p2-hours">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-stone-500">This week</div>
                <div className="flex items-end gap-1.5 mt-1">
                  <span className="font-display font-black text-5xl leading-none">32.5</span>
                  <span className="text-xs font-black uppercase mb-1">hrs</span>
                </div>
              </div>
              <div className="text-right text-[11px] font-bold text-stone-600 leading-relaxed">
                24.0 approved<br />8.5 pending
              </div>
            </div>
            <button className={`mt-4 w-full h-16 bg-[#FF5F15] border-2 border-black rounded-xl text-white flex items-center justify-between px-5 ${shadow} ${crush}`} data-testid="p2-log-hours">
              <span className="font-display text-xl font-black uppercase tracking-tight">Log your hours</span>
              <span className="w-10 h-10 rounded-full bg-white text-black border-2 border-black flex items-center justify-center">
                <Clock className="w-5 h-5" strokeWidth={2.5} />
              </span>
            </button>
          </div>

          {/* Quick actions — 3-col chunky grid */}
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.25em] text-stone-500 mb-2">Quick actions</div>
            <div className="grid grid-cols-3 gap-3" data-testid="p2-actions">
              {[
                [ShieldAlert, "Hazard", "#FF3B30", "text-white"],
                [ClipboardCheck, "QA", "#FFD700", "text-black"],
                [ClipboardList, "Forms", "#FFFFFF", "text-black"],
                [FileSignature, "Variation", "#FFFFFF", "text-black"],
                [Palmtree, "Leave", "#FFFFFF", "text-black"],
                [Trophy, "Boards", "#FFFFFF", "text-black"],
              ].map(([Icon, t, bg, tc]) => (
                <button key={t} style={{ backgroundColor: bg }}
                  className={`${tc} border-2 border-black rounded-2xl aspect-square flex flex-col items-center justify-center gap-2 ${shadow} ${crush}`}>
                  <Icon className="w-7 h-7" strokeWidth={2.5} />
                  <span className="text-[11px] font-black uppercase tracking-wide">{t}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Attention strip */}
          <div className={`bg-black text-white border-2 border-black rounded-2xl px-4 py-3 flex items-center justify-between ${shadow}`} data-testid="p2-attention">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD700]">Needs you</div>
              <div className="text-sm font-bold mt-0.5">2 timesheets pending · 1 open hazard</div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#FFD700]" strokeWidth={2.5} />
          </div>
        </div>

        {/* Floating pill bottom nav */}
        <div className="absolute bottom-3 inset-x-4" data-testid="p2-bottomnav">
          <div className={`bg-white border-2 border-black rounded-2xl grid grid-cols-6 py-2.5 ${shadow}`}>
            {[[Home, "Home", true], [Briefcase, "Jobs"], [Clock, "Hours"], [ShieldAlert, "Safety"], [Wrench, "Tools"], [CircleUser, "Me"]].map(([Icon, l, active]) => (
              <div key={l} className="flex flex-col items-center gap-0.5">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${active ? "bg-black text-[#FFD700]" : "text-stone-500"}`}>
                  <Icon className="w-4.5 h-4.5 w-[18px] h-[18px]" strokeWidth={2.5} />
                </span>
                <span className={`text-[8px] font-black uppercase tracking-wider ${active ? "text-black" : "text-stone-400"}`}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="text-center text-zinc-600 text-xs font-mono mt-4 uppercase tracking-widest">Preview B — Daylight High-Vis (light)</div>
    </div>
  );
}
